// Copyright © 2026 Phaena Management B.V. All rights reserved.
// Proprietary and confidential. See COPYRIGHT.md in the repository root.

/**
 * Landing — the "first 5 seconds" page for text-analytics.
 *
 * Above the fold: wordmark, tagline with a highlighter sweep, the
 * privacy promise, "Open the Reader" + "How privacy works" CTAs,
 * a 3-stat ribbon. Below: value props, the two-walls privacy teaser,
 * bottom CTA. Content is drawn from the product README — local-first,
 * Dutch open-text survey analytics, PII-gated, AVG/GDPR-aligned.
 */

import { Link } from "react-router-dom"
import { Wordmark } from "@/brand/Wordmark"
import { READER_URL, PORTAL_URL } from "@/lib/links"
import {
  ArrowRight,
  ArrowUpRight,
  FileSpreadsheet,
  Languages,
  Lock,
  ScanSearch,
  ShieldCheck,
  Sparkles,
} from "lucide-react"

export function Landing() {
  return (
    <div className="flex flex-col">
      <Hero />
      <StatRibbon />
      <ValueProps />
      <PrivacyTeaser />
      <BottomCta />
    </div>
  )
}

// ── Hero ──────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 80% at 18% 28%, oklch(0.86 0.15 90 / 0.12), transparent 70%)," +
            "radial-gradient(50% 60% at 86% 78%, oklch(0.42 0.07 190 / 0.10), transparent 70%)",
        }}
      />
      <div className="mx-auto max-w-7xl px-6 pt-24 pb-20 sm:pt-28 sm:pb-24">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-8">
          <span className="inline-block w-6 h-px bg-primary" />
          Open-text survey analytics, browser-native
        </div>
        <h1
          className="font-display text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[1.06] text-foreground max-w-5xl"
          style={{ fontFamily: '"Spectral", "Iowan Old Style", Georgia, serif' }}
        >
          Read every open answer.
          <br />
          <span className="relative inline-block">
            <span
              aria-hidden
              className="absolute inset-x-[-2px] bottom-1 h-[0.42em] -z-10 rounded-sm animate-highlight-sweep"
              style={{ background: "oklch(0.86 0.15 90 / 0.85)" }}
            />
            Keep every secret.
          </span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
          <Wordmark
            size="sm"
            iconOnly
            className="text-primary inline-block translate-y-0.5 mr-1"
          />
          <span className="text-foreground font-medium">Wren</span>{" "}
          turns hundreds of Dutch free-text survey answers into topics,
          sentiment, and PII flags — computed entirely in your browser via a
          WebAssembly engine. No upload, no cloud, no API keys. Your
          respondents&rsquo; sentences never leave the page.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <a
            href={READER_URL}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity shadow-sm"
          >
            <Sparkles className="h-4 w-4" />
            Open the Reader
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <Link
            to="/privacy"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-border bg-card text-foreground font-medium hover:bg-secondary/60 transition-colors"
          >
            How privacy works
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Access is licensed through your workspace.{" "}
          <a
            href={PORTAL_URL}
            className="underline underline-offset-2 hover:text-foreground"
          >
            Request access
          </a>
          .
        </p>
      </div>
    </section>
  )
}

// ── Stat ribbon ───────────────────────────────────────────────────────

const STATS: { value: string; label: string }[] = [
  { value: "0", label: "bytes uploaded — analysis runs in your tab" },
  { value: "9", label: "Dutch PII detectors, checksum-validated" },
  { value: ".sav · .csv", label: "exports that pivot in Excel / SPSS" },
]

function StatRibbon() {
  return (
    <section className="border-y border-border bg-card/50">
      <div className="mx-auto max-w-7xl px-6 py-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
        {STATS.map((s) => (
          <div key={s.label} className="flex flex-col gap-1">
            <span
              className="font-display text-3xl text-primary"
              style={{ fontFamily: '"Spectral", Georgia, serif' }}
            >
              {s.value}
            </span>
            <span className="text-sm text-muted-foreground">{s.label}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── Value props ───────────────────────────────────────────────────────

const PROPS: { icon: typeof Lock; title: string; body: string }[] = [
  {
    icon: Lock,
    title: "In your browser",
    body: "A WASM Rust engine parses every answer client-side. Verifiable in DevTools — the Network panel stays at zero after load.",
  },
  {
    icon: Languages,
    title: "Dutch-native",
    body: "Lemma dictionary, multi-word expressions and curated lexicons tuned for Dutch survey language — not a generic translate-then-guess pipeline.",
  },
  {
    icon: ScanSearch,
    title: "Topics & sentiment",
    body: "Drop a rule pack and a corpus; get per-respondent topics, polarity and missing-answer classification, joinable back to your dataset by ID.",
  },
  {
    icon: ShieldCheck,
    title: "PII-safe by default",
    body: "BSN, IBAN, e-mail, phone and more are detected and redacted before you ever read a row. BSN and IBAN are checksum-validated, not just regex-matched.",
  },
  {
    icon: FileSpreadsheet,
    title: "Exports that pivot",
    body: "Download a wide .sav or .csv — original columns plus derived topic and sentiment columns — and pivot immediately in Excel or SPSS.",
  },
  {
    icon: ScanSearch,
    title: "Auditable",
    body: "Every run carries a SHA-256 receipt: same pack + same input = same output. Evidence a DPO can attach to a processing agreement.",
  },
]

function ValueProps() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-20">
      <h2
        className="font-display text-3xl sm:text-4xl tracking-tight text-foreground mb-3"
        style={{ fontFamily: '"Spectral", Georgia, serif' }}
      >
        The open-text column nobody reads — finally read.
      </h2>
      <p className="text-muted-foreground max-w-2xl mb-12">
        You ran a survey. The multiple-choice answers were easy. The one
        free-text question has been sitting in a column because going through
        hundreds of responses by hand is awful. This is the third option —
        local, fast, and worry-free.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {PROPS.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="rounded-lg border border-border bg-card p-6 hover:border-primary/40 transition-colors"
          >
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-secondary text-primary mb-4">
              <Icon className="h-5 w-5" />
            </div>
            <h3 className="font-medium text-foreground mb-1.5">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

// ── Privacy teaser ────────────────────────────────────────────────────

function PrivacyTeaser() {
  return (
    <section className="border-y border-border bg-secondary/40">
      <div className="mx-auto max-w-7xl px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-4">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Privacy before exploration
          </div>
          <h2
            className="font-display text-3xl sm:text-4xl tracking-tight text-foreground mb-4"
            style={{ fontFamily: '"Spectral", Georgia, serif' }}
          >
            &ldquo;Trust us&rdquo; doesn&rsquo;t satisfy a DPO.
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            Nothing is uploaded, because there is nowhere to upload to. The PII
            gate scans every answer before it is shown, redacting identifiers
            and flagging the rest. What happened is provable — not asserted.
          </p>
          <Link
            to="/privacy"
            className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
          >
            See how the privacy walls work
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-4">
          <Wall
            n="1"
            title="No egress, structurally"
            body="The Reader is a static page. It opens your file, parses it in WebAssembly, and hands you a download. There is no server to send data to."
          />
          <Wall
            n="2"
            title="PII gate before your eyes"
            body="Dutch identifiers — BSN, IBAN, e-mail, phone, postcode and more — are detected and redacted before any answer is displayed."
          />
        </div>
      </div>
    </section>
  )
}

function Wall({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 flex gap-4">
      <span
        className="shrink-0 w-8 h-8 rounded-md bg-primary text-primary-foreground inline-flex items-center justify-center font-display text-lg"
        style={{ fontFamily: '"Spectral", Georgia, serif' }}
      >
        {n}
      </span>
      <div>
        <h3 className="font-medium text-foreground mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
      </div>
    </div>
  )
}

// ── Bottom CTA ────────────────────────────────────────────────────────

function BottomCta() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 text-center">
      <Wordmark size="lg" className="text-primary justify-center mb-6" />
      <h2
        className="font-display text-3xl sm:text-4xl tracking-tight text-foreground mb-4"
        style={{ fontFamily: '"Spectral", Georgia, serif' }}
      >
        Turn 1,000 free answers into one screen.
      </h2>
      <p className="text-muted-foreground max-w-xl mx-auto mb-8">
        Open the Reader, drop a free starter pack and your survey export, and
        read your open-text column for the first time — without it ever leaving
        your laptop.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <a
          href={READER_URL}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity shadow-sm"
        >
          <Sparkles className="h-4 w-4" />
          Open the Reader
          <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  )
}
